import { createClient } from "@connectrpc/connect";
import { grpcTransportProvider } from "../../../../comun/services/grpc/GrpcTransport";
import { mapConnectError } from "../../../../comun/services/grpc/mapConnectError";
import { createCachedFactory } from "../../../../utils/factoryUtils";
import { serviceSync } from "../../../../base/services/ServiceSync";
import {
  MonitoreoSesionDashboardSesionItem,
  MonitoreoSesionService,
} from "../../../../generated/grpc/monitoreo_sesion_pb";
import { grpcMonitoreoSesionDatabase } from "../database/GrpcMonitoreoSesionDatabase";
import { SesionDashboardResponse } from "../response/SesionDashboardResponse";

/** Item guardado localmente: `MonitoreoSesionDashboardSesionItem` (gRPC) +
 *  `fincaId` inyectado acá -no viaja en el mensaje, el cliente ya sabe qué
 *  finca pidió, mismo criterio que `EscenaGrpcService`-, pero hace falta como
 *  propiedad real del registro para el índice `fincaId` de
 *  `GrpcMonitoreoSesionDatabase`. */
type StoredSesionItem = MonitoreoSesionDashboardSesionItem & { fincaId: string };

/** Variante gRPC-Web de `SesionDashboardGetByFincaIdService` (ver ese
 *  archivo): histórico de sesiones de una finca, con lotes e incidencias ya
 *  calculadas.
 *
 *  **Con delta real (2026-09-15, antes no tenía)**: a diferencia de la
 *  variante REST -que sigue sin sincronización incremental-, esta sincroniza
 *  TODO el histórico de la finca por watermark (`GrpcMonitoreoSesionDatabase`,
 *  delta por `fincaId`) y filtra el año **en memoria**, sobre lo que ya está
 *  en IndexedDB — el server ya no filtra por año (se le quitó ese parámetro
 *  al rpc). Cooldown de sync: 5 minutos (pedido explícito), no las 24h por
 *  defecto de `ServiceSync` — las sesiones de monitoreo se cargan durante el
 *  día de trabajo en campo, un cooldown largo dejaría el dashboard
 *  desactualizado dentro de la misma sesión de página. */
class SesionDashboardGrpcService {
  constructor(private readonly _fincaId: string) {}

  /**
   * Histórico de la finca, ordenado por fecha ascendente.
   *
   * @param anio Año a filtrar (por `fecha`, `yyyy-MM-dd`). Sin él, trae todo
   *             el historial ya sincronizado.
   */
  async getAsync(anio?: number): Promise<SesionDashboardResponse> {
    await this._syncAsync();

    const sesiones = await grpcMonitoreoSesionDatabase.getSesionesByFincaIdAsync<StoredSesionItem>(
      this._fincaId,
    );
    const filtradas = anio === undefined
      ? sesiones
      : sesiones.filter((sesion) => sesion.fecha.slice(0, 4) === String(anio));

    const loteIds = new Set<string>();
    for (const sesion of filtradas) {
      for (const loteId of Object.keys(sesion.datos)) {
        loteIds.add(loteId);
      }
    }
    const lotes = await grpcMonitoreoSesionDatabase.getLotesByIdsAsync([...loteIds]);

    return {
      lotes: lotes.map((lote) => ({ id: lote.id, nombre: lote.nombre })),
      sesiones: filtradas
        .map((sesion) => ({
          id: sesion.id,
          semana: sesion.semana,
          fecha: sesion.fecha,
          isFinalizada: sesion.isFinalizada,
          datos: Object.fromEntries(
            Object.entries(sesion.datos).map(([loteId, incidencia]) => [
              loteId,
              {
                inc: incidencia.valores,
                afectados: incidencia.afectados,
                muestra: incidencia.muestra,
                referencias: incidencia.referencias,
                cultivoId: incidencia.cultivoId,
              },
            ]),
          ),
        }))
        .sort((a, b) => a.fecha.localeCompare(b.fecha)),
    };
  }

  /** Invalida el cooldown para forzar que el próximo `getAsync()` vuelva a
   *  pedir delta al server. */
  clearCache(): void {
    serviceSync.reset(`grpc:monitoreo-sesion-dashboard:${this._fincaId}`);
  }

  private async _syncAsync(): Promise<void> {
    await serviceSync.run(
      `grpc:monitoreo-sesion-dashboard:${this._fincaId}`,
      async () => {
        const ultimaSincronizacion = await grpcMonitoreoSesionDatabase.getUltimaSincronizacionAsync(
          this._fincaId,
        );
        const client = createClient(MonitoreoSesionService, grpcTransportProvider.get());

        let response;
        try {
          response = await client.getDashboardByFincaId({
            fincaId: this._fincaId,
            ultimaSincronizacion,
          });
        } catch (error) {
          throw mapConnectError(error);
        }

        const sesiones: StoredSesionItem[] = response.sesiones.map((sesion) => ({
          ...sesion,
          fincaId: this._fincaId,
        }));
        await grpcMonitoreoSesionDatabase.putDeltaAsync(
          this._fincaId,
          sesiones,
          response.lotes,
          response.timestampSincronizacion,
        );
      },
      5 * 60_000,
    );
  }
}

/** Factory cacheada: una única instancia por fincaId. */
export const fx_createSesionDashboardGrpcService = createCachedFactory(
  (fincaId: string) => new SesionDashboardGrpcService(fincaId),
);
